import { DateTime } from 'luxon';

export const DATE_INPUT_FORMAT: string = 'dd.MM.yyyy';

const ACCEPTED_DATE_INPUT_FORMATS: readonly string[] = [DATE_INPUT_FORMAT, 'd.M.yyyy', 'ddMMyyyy'];

export const toDateTime = (value: string | Date): DateTime =>
  typeof value === 'string' ? DateTime.fromISO(value) : DateTime.fromJSDate(value);

export const formatDateInput = (value?: Date | null): string =>
  value ? DateTime.fromJSDate(value).toFormat(DATE_INPUT_FORMAT) : '';

export const parseDateInput = (text: string): Date | null => {
  const trimmed: string = text.trim();

  if (!trimmed) {
    return null;
  }

  for (const format of ACCEPTED_DATE_INPUT_FORMATS) {
    const parsed: DateTime = DateTime.fromFormat(trimmed, format);

    if (parsed.isValid) {
      return parsed.toJSDate();
    }
  }

  return null;
}

export const toUtcDateOnlyIso = (value?: Date | null): string | null => {
  if (!value) {
    return null;
  }

  const dateTime: DateTime = DateTime.utc(value.getFullYear(), value.getMonth() + 1, value.getDate());

  return dateTime.isValid ? dateTime.toISO() : null;
}

export const isValidAge = (date: string, month: string, year: string): boolean => {
  const birthDate: DateTime = DateTime.fromObject({
    year: Number(year),
    month: Number(month),
    day: Number(date),
  });
  
  if (!birthDate.isValid) {
    return false;
  }
  
  const age: number = Math.floor(DateTime.now().diff(birthDate, 'years').years);
  return age >= 18 && age <= 100;
}

export const formatDateTime = (value?: string | Date): string => {
  if (!value) {
    return '-';
  }

  const dateTime: DateTime = toDateTime(value);

  if (!dateTime.isValid) {
    return typeof value === 'string' ? value : '-';
  }

  return dateTime.toLocaleString(DateTime.DATETIME_MED);
}

export const formatDate = (value?: string | Date): string => {
  if (!value) {
    return '-';
  }

  const dateTime: DateTime = toDateTime(value);

  if (!dateTime.isValid) {
    return typeof value === 'string' ? value : '-';
  }

  return dateTime.toLocaleString(DateTime.DATE_MED)
}

export const formatDateWithRelative = (value?: string | Date): string => {
  if (!value) {
    return '-';
  }

  const dateTime: DateTime = toDateTime(value);

  if (!dateTime.isValid) {
    return typeof value === 'string' ? value : '-';
  }

  const shortDate: string = dateTime.toLocaleString(DateTime.DATE_SHORT);
  const relative: string | null = dateTime.toRelative();

  return relative ? `${shortDate} (${relative})` : shortDate;
}

export const formatTime = (value?: string | Date): string => {
  if (!value) {
    return '-';
  }

  const dateTime: DateTime = toDateTime(value);

  if (!dateTime.isValid) {
    return '-';
  }

  return dateTime.toLocal().toFormat('HH:mm');
}

export const formatTimeOrRelative = (value?: string | Date): string => {
  if (!value) {
    return '';
  }

  const dateTime: DateTime = toDateTime(value).toLocal();

  if (!dateTime.isValid) {
    return '';
  }

  return dateTime.hasSame(DateTime.now(), 'day')
    ? dateTime.toFormat('HH:mm')
    : (dateTime.toRelative() ?? '');
}
export const sortByLatestDate = <T>(items: T[], getDate: (item: T) => string | undefined): T[] => {
  const toMillis = (item: T): number => {
    const date: string | undefined = getDate(item);
    return date ? toDateTime(date).toMillis() : 0;
  };

  return [...items].sort((first: T, second: T) => toMillis(second) - toMillis(first));
}
